// How an author assignment names the category it points at.
//
// A category used to be named by its position, so renaming, reordering, or
// removing one changed what every assignment meant. Categories the author can
// edit now carry a stable id, and assignments store that id.
//
// Three forms are read, because all three exist:
//   - an id, written by the current editor;
//   - "other", meaning the author placed the answer outside every category; and
//   - a position, written before ids existed and still used for descriptor
//     categories, whose order cannot change once a version is pinned.
//
// Only ids (or "other") are written, so positional labels disappear as
// assignments are re-made.

export const OTHER_LABEL = 'other';

const idOf = (category) => {
    const id = category && category.id;
    return typeof id === 'string' && id.trim() !== '' ? id : '';
};

/**
 * The label to store for an assignment to the category at `index`.
 * Falls back to the position when a category has no id, which keeps
 * descriptor-backed categories working unchanged.
 */
export function categoryLabelValue(categories, index) {
    const list = Array.isArray(categories) ? categories : [];
    return idOf(list[index]) || String(index);
}

/**
 * Resolve a stored label to a category position.
 * Returns a number, the string 'other', or null when the label names a category
 * that no longer exists — a null must never be silently read as position 0.
 */
export function labelToIndex(categories, label) {
    if (label === null || label === undefined || label === '') return null;
    if (label === OTHER_LABEL) return OTHER_LABEL;
    const list = Array.isArray(categories) ? categories : [];
    const byID = list.findIndex(category => idOf(category) === String(label));
    if (byID >= 0) return byID;
    // A bare position, from before ids or from a descriptor category.
    if (/^\d+$/.test(String(label))) {
        const index = Number(label);
        return index < list.length ? index : null;
    }
    return null;
}
